import { FileText, Loader2, Megaphone } from "lucide-react";
import React, { useState } from "react";
import { ConfirmDialog } from "src/components/ui/ConfirmDialog";
import { DateTimePicker } from "src/components/ui/DateTimePicker";
import { Input, Textarea } from "src/components/ui/Input";
import { SlabButton } from "src/components/ui/SlabButton";
import { getBroadcastEnvInfo } from "src/lib/notificationRoutes";
import {
  getPortalNotificationFieldErrors,
  summarizePortalNotificationErrors,
  type PortalNotificationFieldErrors,
} from "src/lib/portalNotificationService";
import type {
  PortalNotificationCategory,
  PortalNotificationFormState,
} from "src/types/portalNotifications";
import Collapsible from "../Collapsible";
import { PickerColors, SegmentedPicker } from "../SegmentedPicker";
import CompanyTargetPicker from "./CompanyTargetPicker";

interface Props {
  form: PortalNotificationFormState;
  onChange: (next: PortalNotificationFormState) => void;
  /** Save as draft (create or update the DRAFT row). */
  onSaveDraft: () => void;
  /** Save + publish — makes the notification pop-up eligible. */
  onPublish: () => void;
  saving: boolean;
  publishing: boolean;
  /** True when editing an existing draft row. */
  editingDraft: boolean;
  canWrite: boolean;
  fieldErrors: PortalNotificationFieldErrors;
  error: string | null;
  success: string | null;
}

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs font-semibold text-rose-600" role="alert">
      {message}
    </p>
  );
};

const HEADLINE_MAX = 140;
const BODY_MAX = 600;
const CTA_LABEL_MAX = 40;

const CATEGORY_OPTIONS: {
  value: PortalNotificationCategory;
  label: string;
  desc: string;
}[] = [
  {
    value: "PRODUCT",
    label: "Product",
    desc: "New features, UI changes",
  },
  {
    value: "NEWS",
    label: "News",
    desc: "Announcements, program updates",
  },
  {
    value: "INSTITUTE",
    label: "Institute",
    desc: "Training, certification, education",
  },
];

const CATEGORY_PICKER_COLORS: Record<PortalNotificationCategory, PickerColors> =
  {
    PRODUCT: {
      bg: "#EEF2FF",
      text: "#4F46E5",
      border: "#EEF2FF",
      shadow: "#C7D2FE",
    },
    NEWS: {
      bg: "#FFE7B8",
      text: "#A45A00",
      border: "#FF9600",
      shadow: "#E07B00",
    },
    INSTITUTE: {
      bg: "#D1FAE5",
      text: "#047857",
      border: "#A7F3D0",
      shadow: "#6EE7B7",
    },
  };

const UNSELECTED: PickerColors = {
  bg: "#FFFFFF",
  text: "#777777",
  border: "#E5E5E5",
  shadow: "#D0D0D0",
};

const StepBadge: React.FC<{ n: number }> = ({ n }) => (
  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-teal-500 bg-teal-500 text-xs font-extrabold text-white shadow-[0_2px_0_#0F766E]">
    {n}
  </span>
);

export const PortalNotificationComposer: React.FC<Props> = ({
  form,
  onChange,
  onSaveDraft,
  onPublish,
  saving,
  publishing,
  editingDraft,
  canWrite,
  fieldErrors,
  error,
  success,
}) => {
  const envInfo = getBroadcastEnvInfo(form.env);
  const [confirmClearPortalsOpen, setConfirmClearPortalsOpen] = useState(false);
  const publishErrors = summarizePortalNotificationErrors(
    getPortalNotificationFieldErrors(form),
  );
  const canPublish =
    canWrite && !saving && !publishing && publishErrors === null;
  const update = <K extends keyof PortalNotificationFormState>(
    key: K,
    value: PortalNotificationFormState[K],
  ) => onChange({ ...form, [key]: value });

  const switchToAllPortals = () => {
    onChange({
      ...form,
      targeting: "ALL_COMPANIES",
      targetCompanies: [],
    });
  };

  const buildHeader = (
    step: number,
    title: string,
    desc?: string,
    status?: React.ReactNode,
  ) => (
    <div className="my-4 flex items-start gap-3">
      <StepBadge n={step} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-extrabold text-[#1F1F1F] sm:text-lg">
            {title}
          </h2>
          {status}
        </div>
        {desc && (
          <p className="mt-0.5 text-xs text-[#777777] sm:text-sm">{desc}</p>
        )}
      </div>
    </div>
  );

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (canPublish) onPublish();
      }}
    >
      {form.env === "prod" && (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
          Caution: Production publishes to every targeted portal user. Please
          review before publishing.
        </p>
      )}

      <p className="rounded-lg border border-[#E5E5E5] bg-[#F9FAFB] px-3 py-2 text-xs text-[#6B7280]">
        Publishing to{" "}
        <span className="font-bold text-[#1F1F1F]">{envInfo.label}</span>
        {" — "}
        this deployment&apos;s backend only.
      </p>

      {/* Step 1 — Category */}
      <Collapsible
        header={buildHeader(
          1,
          "Which category?",
          "System notifications are generated by the platform — not authored here.",
        )}
      >
        <SegmentedPicker
          className="px-4 py-4"
          layout="flexible"
          ariaLabel="Category"
          selectedColor={CATEGORY_PICKER_COLORS[form.category]}
          unselectedColor={UNSELECTED}
          options={CATEGORY_OPTIONS.map((o) => o.label)}
          value={
            CATEGORY_OPTIONS.find((o) => o.value === form.category)?.label || ""
          }
          onChange={(label) => {
            const chosen = CATEGORY_OPTIONS.find((o) => o.label === label);
            if (chosen) update("category", chosen.value);
          }}
        />
        <p className="px-4 pb-3 text-xs text-[#9CA3AF]">
          {CATEGORY_OPTIONS.find((o) => o.value === form.category)?.desc}
        </p>
      </Collapsible>

      {/* Step 2 — Content */}
      <Collapsible header={buildHeader(2, "Compose the notification", "")}>
        <div className="space-y-3 px-4 py-4">
          <label className="block">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Headline
              </span>
              <span className="text-[11px] text-[#9CA3AF]">
                {form.headline.length}/{HEADLINE_MAX}
              </span>
            </div>
            <Input
              accent="teal"
              value={form.headline}
              maxLength={HEADLINE_MAX}
              onChange={(e) => update("headline", e.target.value)}
              placeholder="New: Export schedules to CSV"
            />
            <FieldError message={fieldErrors.headline} />
          </label>
          <label className="block">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wide text-[#777777]">
                Body
              </span>
              <span className="text-[11px] text-[#9CA3AF]">
                {form.body.length}/{BODY_MAX}
              </span>
            </div>
            <Textarea
              accent="teal"
              value={form.body}
              maxLength={BODY_MAX}
              onChange={(e) => update("body", e.target.value)}
              placeholder="You can now export your schedule…"
            />
            <FieldError message={fieldErrors.body} />
          </label>
        </div>
      </Collapsible>

      {/* Step 3 — CTA */}
      <Collapsible
        header={buildHeader(
          3,
          "Call to action",
          "Optional button shown on the pop-up and the Hub card. Provide both a label and a URL, or neither.",
        )}
      >
        <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-[1fr_2fr]">
          <label className="block">
            <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
              Button label
            </span>
            <Input
              accent="teal"
              value={form.ctaLabel}
              maxLength={CTA_LABEL_MAX}
              onChange={(e) => update("ctaLabel", e.target.value)}
              placeholder="Learn more"
            />
            <FieldError message={fieldErrors.ctaLabel} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
              URL
            </span>
            <Input
              accent="teal"
              type="url"
              value={form.ctaUrl}
              onChange={(e) => update("ctaUrl", e.target.value)}
              placeholder="https://ivueit.com/whats-new"
              className="font-mono text-sm"
            />
            <FieldError message={fieldErrors.ctaUrl} />
          </label>
        </div>
      </Collapsible>

      {/* Step 4 — Schedule */}
      <Collapsible
        header={buildHeader(
          4,
          "Publication window",
          "The pop-up is eligible only between start and end. Times are your local timezone.",
        )}
      >
        <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
              Starts
            </span>
            <DateTimePicker
              accent="teal"
              value={form.startAtLocal || ""}
              onChanged={(next) => update("startAtLocal", next.value)}
            />
            <FieldError message={fieldErrors.startAtLocal} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#777777]">
              Ends
            </span>
            <DateTimePicker
              accent="teal"
              value={form.endAtLocal || ""}
              start={form.startAtLocal || undefined}
              onChanged={(next) => update("endAtLocal", next.value)}
            />
            <FieldError message={fieldErrors.endAtLocal} />
          </label>
        </div>
      </Collapsible>

      {/* Step 5 — Targeting */}
      <Collapsible
        header={buildHeader(
          5,
          "Who should see it?",
          "All portals shows it to every Client Portal user; specific portals limits it to selected companies.",
        )}
      >
        <div className="grid grid-cols-1 gap-2 px-4 py-4 sm:grid-cols-2">
          {(
            [
              { value: "ALL_COMPANIES", label: "All portals" },
              { value: "SPECIFIC_COMPANIES", label: "Specific portals" },
            ] as const
          ).map((opt) => {
            const active = form.targeting === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  if (opt.value === "ALL_COMPANIES") {
                    if (form.targeting === "ALL_COMPANIES") return;
                    if (form.targetCompanies.length > 0) {
                      setConfirmClearPortalsOpen(true);
                      return;
                    }
                    switchToAllPortals();
                    return;
                  }
                  update("targeting", opt.value);
                }}
                aria-pressed={active}
                className={`rounded-2xl border-2 px-3 py-3 text-sm font-extrabold transition-transform active:translate-y-0.5 ${
                  active
                    ? "border-teal-500 bg-teal-50 text-teal-700 shadow-[0_4px_0_#99F6E4]"
                    : "border-[#E5E5E5] bg-white text-[#6B7280] hover:border-[#D0D0D0]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {form.targeting === "SPECIFIC_COMPANIES" && (
          <div className="mt-4 overflow-visible border-t border-dashed border-[#E5E5E5] p-4">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#777777]">
              Portals
            </span>
            <CompanyTargetPicker
              env={form.env}
              selected={form.targetCompanies}
              onChange={(next) => update("targetCompanies", next)}
              disabled={!canWrite}
            />
            <FieldError message={fieldErrors.targeting} />
          </div>
        )}
      </Collapsible>

      <ConfirmDialog
        open={confirmClearPortalsOpen}
        title="Clear selected portals?"
        message="Switching to all portals will clear the portals you've selected. Are you sure you want to continue?"
        confirmLabel="Continue"
        cancelLabel="Cancel"
        destructive
        onConfirm={() => {
          setConfirmClearPortalsOpen(false);
          switchToAllPortals();
        }}
        onCancel={() => setConfirmClearPortalsOpen(false)}
      />

      {/* Actions */}
      <div className="rounded-[1.5rem] border border-[#E5E5E5] bg-white p-5 sm:p-6">
        {error && (
          <div
            className="mb-3 whitespace-pre-line rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700"
            role="alert"
          >
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            {success}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <SlabButton
            type="button"
            variant="softOutline"
            disabled={!canWrite || saving || publishing}
            onClick={onSaveDraft}
            className="w-full sm:w-auto sm:flex-1"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Saving…
              </>
            ) : (
              <>
                <FileText className="h-4 w-4" aria-hidden />
                {editingDraft ? "Update draft" : "Save as draft"}
              </>
            )}
          </SlabButton>
          <SlabButton
            type="submit"
            variant="primary"
            disabled={!canPublish}
            className="w-full sm:w-auto sm:flex-1"
          >
            {publishing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />{" "}
                Publishing…
              </>
            ) : (
              <>
                <Megaphone className="h-4 w-4" aria-hidden />
                Publish
              </>
            )}
          </SlabButton>
        </div>
        {!canWrite && (
          <p className="mt-2 text-center text-xs text-[#777777]">
            You can view portal notifications but not edit or publish them.
          </p>
        )}
        {canWrite &&
          publishErrors &&
          (form.headline.trim() || form.body.trim()) && (
            <p className="mt-2 whitespace-pre-line text-center text-xs text-[#777777]">
              To publish: {publishErrors}
            </p>
          )}
      </div>
    </form>
  );
};

export default PortalNotificationComposer;
